// src/Dashboard/pages/user/TechniquesQuizzDashboard/components/TechniquesMemoryGamePanel.jsx
// Adapte le chemin si besoin.

import React from "react";
import {
  FaCheckCircle,
  FaRedoAlt,
  FaRegEye,
  FaTrophy,
} from "react-icons/fa";

import QuizzProgressBar from "./QuizzProgressBar";

function getGridClass(totalCards) {
  if (totalCards <= 10) {
    return "grid-cols-2 sm:grid-cols-3 md:grid-cols-5";
  }

  if (totalCards <= 20) {
    return "grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 xl:grid-cols-10";
  }

  return "grid-cols-2 sm:grid-cols-4 md:grid-cols-5 xl:grid-cols-10";
}

function MemoryLoadingState() {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-5 xl:grid-cols-10">
      {Array.from({ length: 20 }).map((_, index) => (
        <div
          key={index}
          className="aspect-[3/4] animate-pulse rounded-[22px] bg-slate-200 dark:bg-slate-800"
        />
      ))}
    </div>
  );
}

function MemoryEmptyState({ onRestart }) {
  return (
    <div className="rounded-[24px] border border-dashed border-slate-300 bg-slate-50 px-5 py-12 text-center dark:border-slate-700 dark:bg-slate-800/40">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-white text-slate-400 shadow-sm ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-700">
        <FaRegEye className="text-2xl" />
      </div>

      <h3 className="mt-5 text-lg font-bold text-slate-900 dark:text-white">
        Pas assez de techniques
      </h3>

      <p className="mx-auto mt-2 max-w-xl text-sm leading-6 text-slate-500 dark:text-slate-400">
        Impossible de générer un jeu des familles avec ces critères. Essayez
        d’ajouter d’autres ceintures ou d’activer la self-défense.
      </p>

      <button
        type="button"
        onClick={onRestart}
        className="mt-5 inline-flex items-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-indigo-800"
      >
        <FaRedoAlt />
        Régénérer
      </button>
    </div>
  );
}

function MemoryFinishedPanel({ stats, onRestart }) {
  if (!stats?.finished) return null;

  return (
    <div className="mb-5 overflow-hidden rounded-[24px] border border-emerald-200 bg-emerald-50 shadow-sm dark:border-emerald-800/60 dark:bg-emerald-900/10">
      <div className="p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-start gap-4">
            <div className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-white text-emerald-600 ring-1 ring-emerald-200 dark:bg-slate-950 dark:text-emerald-200 dark:ring-emerald-800/60">
              <FaTrophy className="text-2xl" />
            </div>

            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-emerald-700 dark:text-emerald-200">
                Partie terminée
              </p>

              <h3 className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
                Toutes les paires sont retrouvées
              </h3>

              <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                Résultat :{" "}
                <span className="font-bold text-slate-900 dark:text-white">
                  {stats.moves} coups
                </span>{" "}
                pour {stats.totalPairs} paire{stats.totalPairs > 1 ? "s" : ""}.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onRestart}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-indigo-800"
          >
            <FaRedoAlt />
            Rejouer
          </button>
        </div>
      </div>
    </div>
  );
}

function TechniqueMemoryCard({
  card,
  revealed,
  matched,
  disabled,
  onClick,
}) {
  return (
    <button
      type="button"
      onClick={() => onClick(card)}
      disabled={disabled || revealed || matched}
      className="group relative aspect-[3/4] rounded-[22px] outline-none transition active:scale-[0.98] disabled:cursor-default"
      style={{ perspective: "1000px" }}
      aria-label={revealed ? card.name : "Carte technique masquée"}
    >
      <div
        className="relative h-full w-full transition-transform duration-500"
        style={{
          transformStyle: "preserve-3d",
          transform: revealed || matched ? "rotateY(180deg)" : "rotateY(0deg)",
        }}
      >
        {/* Dos de carte */}
        <div
          className="absolute inset-0 overflow-hidden rounded-[22px] border border-slate-200 bg-gradient-to-br from-[#013E8E] via-indigo-700 to-slate-950 p-3 text-white shadow-sm ring-1 ring-black/5 transition group-hover:-translate-y-0.5 group-hover:shadow-md dark:border-slate-700"
          style={{ backfaceVisibility: "hidden" }}
        >
          <div className="flex h-full flex-col items-center justify-center rounded-[18px] border border-white/15 bg-white/10 text-center backdrop-blur">
            <div className="grid h-14 w-14 place-items-center rounded-2xl bg-white/15 text-2xl font-bold ring-1 ring-white/20">
              柔
            </div>

            <p className="mt-4 text-xs font-bold uppercase tracking-[0.22em] text-white/70">
              Judo
            </p>

            <p className="mt-1 text-sm font-bold">Technique</p>
          </div>
        </div>

        {/* Face de carte */}
        <div
          className={`absolute inset-0 overflow-hidden rounded-[22px] border bg-white shadow-sm ring-1 transition dark:bg-slate-950 ${
            matched
              ? "border-emerald-300 ring-emerald-200 dark:border-emerald-800 dark:ring-emerald-800/60"
              : "border-indigo-200 ring-indigo-100 dark:border-indigo-800/60 dark:ring-indigo-800/40"
          }`}
          style={{
            backfaceVisibility: "hidden",
            transform: "rotateY(180deg)",
          }}
        >
          <div className="flex h-full flex-col">
            <div className="min-h-0 flex-[1.15] bg-slate-50 p-2 dark:bg-slate-900">
              <img
                src={card.image}
                alt={card.name}
                className="h-full w-full object-contain"
                loading="lazy"
              />
            </div>

            <div className="flex min-h-[74px] flex-col justify-center border-t border-slate-100 px-2.5 py-2 text-center dark:border-slate-800">
              <p className="line-clamp-2 text-[13px] font-bold leading-snug text-slate-900 dark:text-white">
                {card.name}
              </p>

              {card.famille && (
                <p className="mt-1 line-clamp-1 text-[10px] font-bold uppercase tracking-wide text-indigo-600 dark:text-indigo-300">
                  {card.famille}
                </p>
              )}
            </div>
          </div>

          {matched && (
            <div className="absolute right-2 top-2 grid h-7 w-7 place-items-center rounded-full bg-emerald-500 text-white shadow-sm">
              <FaCheckCircle className="text-sm" />
            </div>
          )}
        </div>
      </div>
    </button>
  );
}

export default function TechniquesMemoryGamePanel({
  loading,
  cards = [],
  flippedIds = [],
  matchedIds = [],
  locked,
  stats,
  onCardClick,
  onRestart,
}) {
  const gridClass = getGridClass(cards.length);

  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-4 sm:p-5 lg:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Jeu des familles
            </p>

            <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
              Retrouver les paires de techniques
            </h2>

            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Retournez deux cartes. Si les deux techniques sont identiques,
              elles restent visibles. Sinon, elles se cachent automatiquement.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <StatPill label="Coups" value={stats?.moves || 0} />
            <StatPill label="Paires" value={`${stats?.matchedPairs || 0}/${stats?.totalPairs || 0}`} />
            <StatPill label="Reste" value={stats?.remainingPairs || 0} />
          </div>
        </div>

        <div className="mt-5">
          <div className="mb-2 flex items-center justify-between gap-3">
            <p className="text-sm font-bold text-slate-900 dark:text-white">
              Progression
            </p>

            <p className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
              {stats?.progress || 0} %
            </p>
          </div>

          <QuizzProgressBar value={stats?.progress || 0} />
        </div>

        <div className="mt-5">
          {loading ? (
            <MemoryLoadingState />
          ) : cards.length === 0 ? (
            <MemoryEmptyState onRestart={onRestart} />
          ) : (
            <>
              <MemoryFinishedPanel stats={stats} onRestart={onRestart} />

              <div className={`grid gap-3 ${gridClass}`}>
                {cards.map((card) => {
                  const revealed = flippedIds.includes(card.uid);
                  const matched = matchedIds.includes(card.uid);

                  return (
                    <TechniqueMemoryCard
                      key={card.uid}
                      card={card}
                      revealed={revealed}
                      matched={matched}
                      disabled={locked || stats?.finished}
                      onClick={onCardClick}
                    />
                  );
                })}
              </div>

              {!stats?.finished && (
                <div className="mt-5 flex justify-end">
                  <button
                    type="button"
                    onClick={onRestart}
                    className="inline-flex items-center gap-2 rounded-2xl bg-slate-100 px-4 py-3 text-sm font-bold text-slate-700 ring-1 ring-slate-200 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700 dark:hover:bg-slate-700"
                  >
                    <FaRedoAlt />
                    Mélanger à nouveau
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
}

function StatPill({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-center dark:border-slate-700 dark:bg-slate-800/60">
      <p className="text-lg font-bold text-slate-900 dark:text-white">
        {value}
      </p>

      <p className="mt-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </p>
    </div>
  );
}